import {
  selectGridPanel,
  selectMaximizedStream,
} from 'features/layout/features/content/contentSlice';
import {
  selectContentRatio,
  selectContentWidth,
} from 'features/layout/features/contentDimensions/contentDimensionsSlice';
import { selectPaginationTotalStreams } from 'features/layout/features/pagination/paginationSlice';
import { selectCustomTilesCount } from 'features/layout/features/config/configSlice';
import { selectDeviceType } from 'features/application/applicationSlice';
import { resolveMinContentWidth } from 'features/layout/resolveMinContentWidth';
import { useDimensionsObserver } from 'features/layout/useDimensionsObserver';
import { useBreakpoints } from 'hooks/useBreakpoints';
import React, { useMemo } from 'react';
import { useAppSelector } from 'store/hooks';
import { RoomLayoutValuesProvider } from 'features/layout/Context';
import { verticalStageHeight } from 'utils/layout';

const DimensionsWatcher = ({ children }: { children: React.ReactElement }) => {
  useDimensionsObserver();

  if (!children) {
    return null;
  }

  return children;
};

interface LayoutProviderProps {
  size: { width: number | undefined; height: number | undefined };
  children: React.ReactElement;
}

export const LayoutProvider = ({ size: layoutSize, children }: LayoutProviderProps) => {
  const defaultWidth = window.innerWidth;
  const defaultHeight = window.innerHeight;

  const rawContentWidth = useAppSelector(selectContentWidth);
  const deviceType = useAppSelector(selectDeviceType);
  const totalStreams = useAppSelector(selectPaginationTotalStreams);
  const customTilesCount = useAppSelector(selectCustomTilesCount);

  const { isMobile, isPortrait } = useBreakpoints();

  const maximizedStream = useAppSelector(selectMaximizedStream);
  const gridPanel = useAppSelector(selectGridPanel);
  const hasContent = !!maximizedStream;

  const contentRatio = useAppSelector((state) =>
    maximizedStream ? selectContentRatio(state, maximizedStream) : 0
  );

  const roomWidth = layoutSize.width || defaultWidth;
  const roomHeight = layoutSize.height || defaultHeight;

  const verticalLayout = useMemo(
    () => (isMobile && isPortrait) || roomHeight > roomWidth,
    [isMobile, isPortrait, roomWidth, roomHeight]
  );

  const minContentWidth = useMemo(
    () =>
      resolveMinContentWidth({
        verticalLayout,
        maximizedStream,
        gridPanel,
        roomWidth,
        roomHeight,
        tileCount: totalStreams + customTilesCount,
        contentRatio,
        deviceType,
      }),
    [
      verticalLayout,
      maximizedStream,
      gridPanel,
      roomWidth,
      roomHeight,
      totalStreams,
      customTilesCount,
      contentRatio,
      deviceType,
    ]
  );

  const contentWidth = Math.max(rawContentWidth, minContentWidth);

  // @FIXME: these calculations could be improved so "vertical layout" is more straightforward. Maybe a separate AppLayout?;
  const tilesWidth = verticalLayout
    ? roomWidth
    : roomWidth - (hasContent ? contentWidth : 0) * roomWidth;

  const stageHeight = verticalStageHeight(roomWidth);

  const tilesHeight =
    verticalLayout && hasContent ? roomHeight - stageHeight : roomHeight || defaultHeight;

  const contentHeight = verticalLayout ? stageHeight : roomHeight;

  const value = useMemo(
    () => ({
      roomWidth,
      roomHeight,
      contentHeight,
      tilesHeight,
      tilesWidth,
      verticalLayout,
      contentWidth,
      minContentWidth,
    }),
    [
      roomWidth,
      roomHeight,
      contentHeight,
      tilesHeight,
      tilesWidth,
      verticalLayout,
      contentWidth,
      minContentWidth,
    ]
  );

  return (
    <RoomLayoutValuesProvider value={value}>
      <DimensionsWatcher>{children}</DimensionsWatcher>
    </RoomLayoutValuesProvider>
  );
};
